import React, { useState, useRef, useEffect, useCallback } from "react";
import "./App.css";
import chatData from "./chat.json";

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Welcome to the Frequency Chain Assistant! Ask me anything about Frequency Chain, our green blockchain technology, or the FRQCY token.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [loginData, setLoginData] = useState({ name: "", email: "", phone: "" });
  const [loginError, setLoginError] = useState("");
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const loadingInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(loadingInterval);
          return 100;
        }
        return prev + Math.floor(Math.random() * 15) + 5;
      });
    }, 200);

    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
      clearInterval(loadingInterval);
    }, 2500);

    return () => {
      clearTimeout(loadingTimer);
      clearInterval(loadingInterval);
    };
  }, []);

  const getBotResponse = useCallback((userInput) => {
    const input = userInput.toLowerCase().trim();
    
    // Check for exact matches first
    const matchedIntent = chatData.intents.find(intent => 
      intent.keywords.some(keyword => 
        input.includes(keyword.toLowerCase())
      )
    );

    if (matchedIntent) {
      return matchedIntent.response;
    }

    // If no exact match, check for partial keyword matches
    const words = input.split(/\s+/);
    const relatedIntents = chatData.intents
      .map(intent => {
        const relevance = intent.keywords.reduce((score, keyword) => {
          const keywordWords = keyword.toLowerCase().split(/\s+/);
          const wordMatches = words.filter(word => 
            keywordWords.some(kWord => 
              kWord.includes(word) || word.includes(kWord)
            )
          );
          return score + wordMatches.length;
        }, 0);
        return { intent, relevance };
      })
      .filter(item => item.relevance > 0)
      .sort((a, b) => b.relevance - a.relevance);

    if (relatedIntents.length > 0 && relatedIntents[0].relevance >= 2) {
      return relatedIntents[0].intent.response;
    }

    return chatData.fallback;
  }, []);

  const handleSend = useCallback(
    (text) => {
      if (!text.trim()) return;
      setMessages((prev) => [...prev, { sender: "user", text }]);
      setInput("");
      setIsTyping(true);

      setTimeout(() => {
        const reply = getBotResponse(text);
        setMessages((prev) => [...prev, { sender: "bot", text: reply }]);
        setIsTyping(false);
      }, 600 + Math.random() * 300);
    },
    [getBotResponse]
  );

  const formatMessageText = useCallback((text) => {
    return text.split('\n').map((paragraph, i) => (
      <p key={i}>{paragraph}</p>
    ));
  }, []);

  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      const handleKeyDown = (e) => {
        if (e.key === "Escape") inputRef.current?.blur();
        if (e.key === "/" && e.target.tagName !== "INPUT") {
          e.preventDefault();
          inputRef.current?.focus();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isLoading]);

  const validateLogin = () => {
    const { name, email, phone } = loginData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10,15}$/;
    if (!name.trim()) {
      setLoginError("Name is required.");
      return false;
    }
    if (!emailRegex.test(email)) {
      setLoginError("Invalid email address.");
      return false;
    }
    if (!phoneRegex.test(phone)) {
      setLoginError("Invalid phone number.");
      return false;
    }
    setLoginError("");
    return true;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (validateLogin()) {
      setShowLogin(false);
      // Send login data to backend
      try {
        await fetch('http://localhost:5000/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(loginData),
        });
      } catch (err) {
        // Optionally show an error to the user
        console.error("Failed to save login to backend:", err);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <img
            src="/logo.png"
            alt="Frequency Chain Logo"
            className="loading-logo"
            onError={(e) => (e.target.style.display = "none")}
          />
          <h2 className="loading-title">Frequency Chain</h2>
          <p className="loading-subtitle">Decentralized Communication Protocol</p>
          <div className="loading-status">
            <div className="loading-dots">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
            <div className="loading-progress-container">
              <div 
                className="loading-progress-bar" 
                style={{ width: `${loadingProgress}%` }}
              ></div>
              <span className="loading-progress-text">{loadingProgress}%</span>
            </div>
            <p className="loading-message">Initializing chat interface...</p>
          </div>
        </div>
      </div>
    );
  }

  if (showLogin && !isLoading) {
    return (
      <div className="login-screen">
        <div className="login-box">
          <h2 className="login-title">Frequency Chain Login</h2>
          <form className="login-form" onSubmit={handleLoginSubmit}>
            <input
              type="text"
              placeholder="Name"
              value={loginData.name}
              onChange={e => setLoginData({ ...loginData, name: e.target.value })}
              className="login-input"
              required
            />
            <input
              type="email"
              placeholder="Email"
              value={loginData.email}
              onChange={e => setLoginData({ ...loginData, email: e.target.value })}
              className="login-input"
              required
            />
            <input
              type="tel"
              placeholder="Phone Number"
              value={loginData.phone}
              onChange={e => setLoginData({ ...loginData, phone: e.target.value })}
              className="login-input"
              required
            />
            {loginError && <div className="login-error">{loginError}</div>}
            <button type="submit" className="login-btn">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <header className="chat-header">
        <img
          src="/logo.png"
          alt="Frequency Chain Logo"
          className="logo"
          onError={(e) => (e.target.style.display = "none")}
        />
        <h1>Frequency Chain Assistant</h1>
      </header>

      <main className="chat-box">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`message-row ${msg.sender === "user" ? "right" : "left"}`}
            aria-live={i === messages.length - 1 ? "polite" : "off"}
          >
            <div className={`message ${msg.sender}`}>
              {formatMessageText(msg.text)}
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="message-row left" aria-live="polite">
            <div className="message bot typing-indicator">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}

        <div ref={chatEndRef} aria-hidden="true" />
      </main>

      <div className="input-area">
        <input
          ref={inputRef}
          type="text"
          value={input}
          placeholder="Type your question... (Press / to focus)"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend(input)}
          disabled={isTyping}
          aria-label="Chat input"
        />
        <button
          onClick={() => handleSend(input)}
          disabled={!input.trim() || isTyping}
          aria-label="Send message"
        >
          {isTyping ? "..." : "➤"}
        </button>
      </div>
    </div>
  );
}

export default App;